import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
// eslint-disable-next-line no-unused-vars
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const TrendChart = ({ data }) => {
  // Ensure data is an array
  const trendData = Array.isArray(data) ? data : [];

  if (!data || trendData.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            Privilege Creep Trend (30 Days)
          </Typography>
        </Box>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <CalendarMonthIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2">No trend data available</Typography>
            <Typography variant="caption">Run privilege creep analysis to see trends</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Transform data for recharts
  const chartData = trendData.map(item => ({
    date: new Date(item.analysis_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    critical: item.high_risk_count || 0,
    moderate: item.moderate_risk_count || 0,
    low: item.low_risk_count || 0,
    appropriate: item.appropriate_count || 0,
    total: item.users_analyzed || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>{label}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block', mb: 0.5 }}>
              <strong>{entry.name}:</strong> {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          Privilege Creep Trend (30 Days)
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Shows how privilege creep levels have changed over time
        </Typography>
      </Box>
      <CardContent sx={{ p: 2, pt: 1 }}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#94a3b8', fontSize: 11 }}
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
            <Line type="monotone" dataKey="critical" name="Critical" stroke="#d27373" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="moderate" name="Moderate" stroke="#d4925e" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="low" name="Low" stroke="#5da87a" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="appropriate" name="Appropriate" stroke="#2563EB" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} strokeDasharray="5 5" />
          </LineChart>
        </ResponsiveContainer>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Based on {trendData.length} run{trendData.length !== 1 ? 's' : ''} in the last 30 days
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrendChart;
